import { z } from 'zod';

/**
 * Zod schemas for the BP-03 `/dpdp/*` responses (ARCHITECTURE §3 Validation: "Zod on
 * the web from the same OpenAPI").
 *
 * They are hand-written rather than derived from `src/lib/api/schema.d.ts`: that file is
 * generated by `openapi-typescript` in CI from an OpenAPI document that only contains a
 * route once `app/main.py` mounts it, and mounting BP-03's routers is the integrator's
 * step (see `api/app/modules/consent/README.md`). Until the generated types carry these
 * schemas, the fetcher still validates every body against a contract — which is the point
 * of the Zod layer — and `features/dpdp/api.test.ts` pins the field names against the
 * pydantic models' own spelling.
 *
 * **No client PII here.** A DSR row carries a client *id*, a type and a status; a breach
 * carries ids and counts; the processing log carries the *names* of the fields that were
 * read. The export object key is absent from every one of them (BP-03 §9, rule 9).
 */

/** One bullet of the notice: its i18n key and its text in the requested locale. */
export const noticeItemSchema = z.object({ key: z.string(), text: z.string() });

/** One section of the notice (BP-03 rule 7). */
export const noticeSectionSchema = z.object({
  key: z.string(),
  title: z.string(),
  body: z.array(z.string()),
  items: z.array(noticeItemSchema),
});

/** The published grievance officer (rule 7, §14.2). */
export const noticeGrievanceSchema = z.object({
  source: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
});

/**
 * The notice version currently released by the api (`notice.NOTICE_TEXT_HASHES`'s last
 * entry). BP-20 released `en-v2`, which is what made this a constant rather than a
 * literal in one component: a screen that records consent evidence names the notice it
 * showed, and a stale literal there records the wrong document against a client's grant.
 *
 * `api/tests/consent/test_notice_bundle.py::test_the_web_knows_the_released_notice_version`
 * pins it against the Python side, the same way the `dpdp.json` bundle is pinned.
 */
export const RELEASED_NOTICE_VERSION = 'en-v2';

/** `GET /dpdp/notice` — the whole document plus what the page has to show. */
export const noticeSchema = z.object({
  version: z.string(),
  locale: z.string(),
  sections: z.array(noticeSectionSchema),
  grievance: noticeGrievanceSchema,
  board_url: z.string(),
});

/** One data-principal request, with rule 8's clock already resolved by the api. */
export const dsrSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  type: z.string(),
  requested_at: z.string(),
  received_via: z.string(),
  requester_verified: z.boolean(),
  verification_note: z.string().nullable(),
  due_on: z.string(),
  status: z.string(),
  resolution_note: z.string().nullable(),
  fulfilled_at: z.string().nullable(),
  days_left: z.number(),
  due_soon: z.boolean(),
  is_overdue: z.boolean(),
  export_available: z.boolean(),
  export_expires_at: z.string().nullable(),
  correction_payload: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

/** One page of the DSR queue (§6: "sorted by due_on, overdue red"). */
export const dsrPageSchema = z.object({
  items: z.array(dsrSchema),
  next_cursor: z.string().nullable(),
});

/** Rule 11's report, per module. */
export const erasureModuleSchema = z.object({
  module: z.string(),
  rows: z.number(),
  fields: z.array(z.string()),
});

export const erasureSchema = z.object({
  request: dsrSchema,
  modules: z.array(erasureModuleSchema),
  skipped: z.array(z.string()),
  exports_deleted: z.number(),
  closed: z.boolean(),
});

/**
 * `GET`/`POST /me/erase` — the client's own erasure request (BP-11 R17).
 *
 * Not `dsrSchema` with fields hidden: the api answers a different, thinner
 * type. How the salon verified the requester, who handled it and what note
 * they wrote are its record of its own compliance, and a field that does not
 * exist cannot be shown by a screen that forgets to hide it.
 */
export const myErasureSchema = z.object({
  id: z.string(),
  status: z.string(),
  requested_at: z.string(),
  due_on: z.string(),
  days_left: z.number(),
});

/** `GET /me/erase` answers `null` when nothing is pending. */
export const myErasureOrNullSchema = myErasureSchema.nullable();

/** `POST /dpdp/dsr/{id}/fulfil` — one shape for all three DSR types. */
export const fulfilSchema = z.object({
  request: dsrSchema,
  closed: z.boolean(),
  export_members: z.array(z.string()),
  export_size_bytes: z.number().nullable(),
  erasure: erasureSchema.nullable(),
});

/** `POST /dpdp/dsr/{id}/apply-correction` — the request and the changed field names. */
export const correctionSchema = z.object({
  request: dsrSchema,
  changed: z.array(z.string()),
});

/** Rule 9's link and both of its deadlines. */
export const exportUrlSchema = z.object({
  url: z.string(),
  expires_at: z.string(),
  object_expires_at: z.string().nullable(),
});

/** One breach incident, with Rule 7's 72-hour countdown resolved. */
export const breachSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  detected_at: z.string(),
  data_categories: z.array(z.string()),
  affected_client_ids: z.array(z.string()),
  affected_count: z.number(),
  board_report_due_at: z.string(),
  board_notified_at: z.string().nullable(),
  board_report_ref: z.string().nullable(),
  clients_notified_at: z.string().nullable(),
  client_notice_text: z.string().nullable(),
  status: z.string(),
  closed_at: z.string().nullable(),
  postmortem: z.string().nullable(),
  hours_left: z.number(),
  is_overdue: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const breachListSchema = z.array(breachSchema);

/** Rule 13's preview — §6's `RetentionQueue`. */
export const retentionSchema = z.object({
  dry_run: z.boolean(),
  inactive_candidates: z.array(z.string()),
  inactive_client_months: z.number(),
  consent_rows: z.number(),
  dsr_rows: z.number(),
  exports: z.number(),
  breaches_past_window: z.number(),
});

export type NoticeItem = z.infer<typeof noticeItemSchema>;
export type NoticeSection = z.infer<typeof noticeSectionSchema>;
export type Notice = z.infer<typeof noticeSchema>;
export type Dsr = z.infer<typeof dsrSchema>;
export type DsrPage = z.infer<typeof dsrPageSchema>;
export type Erasure = z.infer<typeof erasureSchema>;
export type MyErasure = z.infer<typeof myErasureSchema>;
export type Fulfil = z.infer<typeof fulfilSchema>;
export type Correction = z.infer<typeof correctionSchema>;
export type ExportUrl = z.infer<typeof exportUrlSchema>;
export type Breach = z.infer<typeof breachSchema>;
export type Retention = z.infer<typeof retentionSchema>;

/** The three rights a data principal exercises (`dsr_request.type`). */
export const DSR_TYPES = ['access', 'correct', 'erase'] as const;
export type DsrType = (typeof DSR_TYPES)[number];

/** How a request arrived (`dsr_request.received_via`). */
export const RECEIVED_VIA = ['in_person', 'whatsapp', 'web', 'email', 'phone'] as const;
export type ReceivedVia = (typeof RECEIVED_VIA)[number];
